// src/services/ticketApi.ts - SIMPLIFIED VERSION (Only actual controller endpoints)
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

// TypeScript interfaces for ticket data
export interface CreateTicketRequest {
  title: string;
  description: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'technical' | 'billing' | 'feature_request' | 'bug' | 'account' | 'training' | 'other';
  createdByName: string;
  createdByEmail: string;
  createdByUserId?: string;
  tags?: string[];
  metadata?: Record<string, any>;
}

export interface AddMessageRequest {
  message: string;
  senderName: string;
  senderEmail: string;
  attachments?: string[];
  metadata?: Record<string, any>;
}

export interface Ticket {
  _id: string;
  businessId: string;
  clientId: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed' | 'duplicate';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'technical' | 'billing' | 'feature_request' | 'bug' | 'account' | 'training' | 'other';
  createdByName: string;
  createdByEmail: string;
  createdByUserId?: string;
  assignedTo?: string;
  assignedToEmail?: string;
  messages: TicketMessage[];
  duplicateOf?: string;
  tags: string[];
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  isDeleted: boolean;
  deletedAt?: string;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessage {
  sender: 'business' | 'support';
  senderName: string;
  senderEmail: string;
  message: string;
  attachments: string[];
  timestamp: string;
  metadata: Record<string, any>;
}

export interface TicketListResponse {
  tickets: Ticket[];
  total: number;
  page: number;
  limit: number;
  success: boolean;
}

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  byPriority: Record<string, number>;
  byCategory: Record<string, number>;
}

export interface TicketFilters {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed' | 'duplicate';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'technical' | 'billing' | 'feature_request' | 'bug' | 'account' | 'training' | 'other';
  page?: number;
  limit?: number;
}

export const ticketApi = createApi({
  reducerPath: 'ticketApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}tickets`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['Tickets', 'TicketStats'],
  endpoints: (builder) => ({
    // 1. Create a new support ticket
    // POST /tickets/business/:businessId
    createTicket: builder.mutation<Ticket, { businessId: string } & CreateTicketRequest>({
      query: ({ businessId, ...ticketData }) => ({
        url: `/business/${businessId}`,
        method: 'POST',
        body: ticketData
      }),
      invalidatesTags: ['Tickets', 'TicketStats']
    }),

    // 2. Get tickets for a business with filtering and pagination
    // GET /tickets/business/:businessId
    getBusinessTickets: builder.query<TicketListResponse, { businessId: string } & TicketFilters>({
      query: ({ businessId, ...filters }) => ({
        url: `/business/${businessId}`,
        params: {
          ...filters
        }
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.tickets.map(({ _id }) => ({ type: 'Tickets' as const, id: _id })),
              { type: 'Tickets', id: 'LIST' },
            ]
          : [{ type: 'Tickets', id: 'LIST' }],
    }),

    // 3. Get a specific ticket for a business
    // GET /tickets/business/:businessId/:ticketId
    getBusinessTicket: builder.query<Ticket, { businessId: string; ticketId: string }>({
      query: ({ businessId, ticketId }) => ({
        url: `/business/${businessId}/${ticketId}`
      }),
      // @ts-ignore
      providesTags: (result, error, { ticketId }) => [
        { type: 'Tickets', id: ticketId }
      ]
    }),

    // 4. Add a message to a ticket
    // POST /tickets/business/:businessId/:ticketId/messages
    addBusinessMessage: builder.mutation<Ticket, { businessId: string; ticketId: string } & AddMessageRequest>({
      query: ({ businessId, ticketId, ...messageData }) => ({
        url: `/business/${businessId}/${ticketId}/messages`,
        method: 'POST',
        body: messageData
      }),
      // @ts-ignore
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'Tickets', id: ticketId },
        { type: 'Tickets', id: 'LIST' }
      ]
    }),

    // 5. Get ticket statistics for a business
    // GET /tickets/business/:businessId/stats
    getBusinessTicketStats: builder.query<TicketStats, { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/business/${businessId}/stats`
      }),
      providesTags: ['TicketStats']
    })
  })
});

// Export hooks for use in components - ONLY the 5 actual controller endpoints
export const {
  // Mutations
  useCreateTicketMutation,
  useAddBusinessMessageMutation,

  // Queries
  useGetBusinessTicketsQuery,
  useGetBusinessTicketQuery,
  useGetBusinessTicketStatsQuery,

  // Lazy queries (for manual triggering)
  useLazyGetBusinessTicketsQuery,
  useLazyGetBusinessTicketQuery
} = ticketApi;

export default ticketApi;